/**
 * Labstep
 *
 * @module grid/SmartTable/coldefs/metadata/options
 * @desc Column Definition for metadata options
 */

import type {
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { getFieldsOptions } from 'labstep-web/components/Metadata/Form/fields';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import {
  suppressKeyboardEventClearCells,
  suppressKeyboardEventDisableEnterOnEdit,
} from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { FormShowEditSelectOptions } from 'labstep-web/core/Form/ShowEdit/SelectOptions';
import { Icon } from 'labstep-web/core/Icon';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { Label } from 'semantic-ui-react';
import styles from './styles.module.scss';

export const getMetadataOptionsColDef = (
  metadata: Metadata,
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
): BatchCreateResourceItemsColDef => {
  const colId = `metadata:${MetadataType.options}:${metadata.guid}`;

  return {
    colId,
    headerName: metadata.label || '',
    isRequired: metadata.is_required ?? false,
    headerComponentParams: {
      icon: ICONS.metadata.type.options,
      cornerIcon: ICONS.resource_item.primary,
    },
    editable: true,
    suppressKeyboardEvent: (params: any): boolean =>
      suppressKeyboardEventDisableEnterOnEdit(params) ||
      suppressKeyboardEventClearCells(params),
    valueClearer: () => null,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata = params.node.data[colId];

      if (!metadata) {
        return null;
      }
      return JSON.stringify(metadata.options?.values);
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const metadataOptions = params.node.data[colId];
      let newValue = params.newValue;

      if (
        (newValue === null || newValue === '') &&
        metadata.is_required
      ) {
        GridBatchCreateResourceItemsService.insertErrorInData(
          data,
          setData,
          params,
          `This field is required.`,
        );
        return false;
      }

      if (metadataOptions && params.newValue == null) {
        metadataOptions.options = null;
      }
      if (typeof newValue === 'string') {
        try {
          newValue = JSON.parse(newValue);
        } catch {
          return false;
        }
      }

      if (!metadataOptions) {
        const row = params.node.data;
        row[colId] =
          GridBatchCreateResourceItemsService.getMetadataBody(
            {
              options: {
                values: newValue,
                is_allow_multiple: true,
                is_allow_add: true,
              },
            },
            metadata,
          );
        data[params.node.rowIndex!] = row;
        setData(data);
        GridBatchCreateResourceItemsService.removeErrorInData(
          data,
          setData,
          params,
        );
        return true;
      }

      const row = params.node.data;
      row[colId] = {
        ...metadataOptions,
        options: {
          ...metadataOptions.options,
          values: newValue,
        },
      };
      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadataOptions = params.node.data[colId];
      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      if (!metadataOptions || !metadataOptions.options) {
        const optionsBody: any = {
          options: {
            values: [],
            is_allow_multiple: true,
            is_allow_add: true,
          },
        };

        if (metadata) {
          optionsBody.options.is_allow_multiple =
            metadata.options?.is_allow_multiple !== undefined
              ? metadata.options.is_allow_multiple
              : true;

          optionsBody.options.is_allow_add =
            metadata.options?.is_allow_add !== undefined
              ? metadata.options.is_allow_add
              : true;
        }

        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <TextPlaceholder
              onClick={() => {
                const row = params.node.data;
                row[colId] =
                  GridBatchCreateResourceItemsService.getMetadataBody(
                    {
                      options: {
                        values: {},
                        is_allow_multiple:
                          metadata?.options?.is_allow_multiple !==
                          undefined
                            ? metadata.options.is_allow_multiple
                            : true,
                        is_allow_add:
                          metadata?.options?.is_allow_add !==
                          undefined
                            ? metadata.options.is_allow_add
                            : true,
                      },
                    },
                    metadata,
                  );
                data[params.node.rowIndex!] = row;
                setData(data);
                GridBatchCreateResourceItemsService.removeErrorInData(
                  data,
                  setData,
                  params,
                );
                GridBatchCreateResourceItemsService.startEditingCell(
                  params,
                );
              }}
            >
              Select
            </TextPlaceholder>
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </span>
        );
      }
      return (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div>
            {' '}
            {Object.values(metadataOptions?.options?.values || [])
              .length ? (
              Object.keys(metadataOptions?.options?.values || [])
                .filter(
                  (key) => metadataOptions?.options?.values[key],
                )
                .map((value) => (
                  <Label key={`${value}`} circular>
                    {value}
                  </Label>
                ))
            ) : (
              <DataGridPlaceholder
                children="Select"
                params={params}
                editable
              />
            )}
          </div>
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </span>
      );
    },

    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const row = params.node.data;

      const metadataOptions = params.node.data[colId];

      if (!metadataOptions) {
        stopEditingAndFocus(params);
        return '';
      }

      return (
        <div
          className={styles.container}
          style={{
            width: params.column.getActualWidth(),
          }}
        >
          <ReadOnMountHOC
            type="entities"
            entityName={Metadata.entityName}
            params={{
              id: metadata.idAttr,
              get_single: 1,
            }}
            loading={{ loader: 'placeholder' }}
            children={({ entity: metadataTemplate }) => {
              return (
                <FormShowEditSelectOptions
                  canEdit={true}
                  children={null}
                  initialEditingState
                  onToggle={(): void => stopEditingAndFocus(params)}
                  onSubmit={(
                    submittedValue: any,
                    updateAction: any,
                  ) => {
                    const rowDataKeys =
                      Object.keys(row[colId].options?.values).length >
                      0
                        ? Object.keys(row[colId].options?.values)
                        : Object.keys(
                            metadataTemplate.options?.values,
                          );

                    const submittedValuesKeys = Object.keys(
                      submittedValue.values,
                    );

                    if (
                      rowDataKeys.length !==
                        submittedValuesKeys.length ||
                      submittedValuesKeys.some(
                        (key) => !rowDataKeys.includes(key),
                      )
                    ) {
                      updateAction();
                    }
                    row[colId] = {
                      ...metadataOptions,
                      options: submittedValue,
                    };
                    data[params.node.rowIndex!] = row;

                    setData(data);
                    GridBatchCreateResourceItemsService.removeErrorInData(
                      data,
                      setData,
                      params,
                    );

                    params.api.stopEditing();
                  }}
                  field={
                    getFieldsOptions(
                      metadataTemplate,
                      true,
                      metadataTemplate.is_template,
                      true,
                    )[0]
                  }
                  entity={metadataTemplate}
                  entityName={Metadata.entityName}
                />
              );
            }}
          />
        </div>
      );
    },
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
  };
};
